import React, { useState } from "react";
import { ReactComponent as DiagnosisItem } from "../../../assets/images/diagnosis-item-logo.svg";
import { ReactComponent as ChevronRight } from "../../../assets/images/chevron-right.svg";
import { ReactComponent as ThumbsUp } from "../../../assets/images/thumbs-up-outline.svg";
import { ReactComponent as ThumbsDown } from "../../../assets/images/thumbs-down-outline.svg";
import { ReactComponent as Like } from "../../../assets/images/like.svg";
import { ReactComponent as Dislike } from "../../../assets/images/dislike.svg";
import { ReactComponent as LabTest } from "../../../assets/images/lab-test.svg";
import { postFeedback } from "../service";
import { trackZymedEvent, ZYMED_EVENTS } from "../../../utils/zymedAnalytics";

type DiagnosisViewProps = {
  generatedDDx: any;
  disabled?: boolean;
  /** The generated case these diagnoses belong to; absent until the backend returns it. */
  caseId?: string;
};

const DiagnosisView: React.FC<DiagnosisViewProps> = ({
  generatedDDx,
  disabled = false,
  caseId,
}) => {
  const [activeTab, setActiveTab] = useState<
    "most-likely" | "expanded" | "cannot-miss"
  >("most-likely");

  const segregateByLikelihood = (diagnoses: any) =>
    (diagnoses || []).reduce(
      (
        acc: { [x: string]: { likelihood: any }[] },
        { likelihood, ...rest }: any
      ) => {
        const key =
          likelihood === "can't miss"
            ? "cannot-miss"
            : likelihood === "most likely"
              ? "most-likely"
              : "expanded";
        acc[key] = acc[key] || [];
        acc[key].push({ likelihood: key, ...rest });
        return acc;
      },
      {}
    );

  const mapDiagnosesToFeedbacks = (data: { [x: string]: any }) => {
    return Object.fromEntries(
      data
        .filter((diagnosis: { hasOwnProperty: (arg0: string) => any }) =>
          diagnosis.hasOwnProperty("impression")
        )
        .map(({ _id, impression }: { _id: string; impression: string }) => [
          _id,
          impression,
        ])
    );
  };

  const segregatedDiagnoses = segregateByLikelihood(generatedDDx);
  const [feedbacks, setFeedbacks] = useState<any>(
    mapDiagnosesToFeedbacks(generatedDDx)
  );

  const [opened, setOpened] = useState<number[]>([]);

  const toggleOpened = (indexToToggle: number) => {
    if (opened.includes(indexToToggle)) {
      setOpened(opened.filter((i) => i !== indexToToggle));
    } else {
      setOpened([...opened, indexToToggle]);
    }
  };

  const onPressFeedback = async (
    resultId: string,
    feedback: "like" | "dislike" | null
  ) => {
    // Clearing a reaction (feedback === null) is not a taxonomy event.
    if (feedback) {
      trackZymedEvent(ZYMED_EVENTS.DDX_RESPOND, {
        case_id: caseId,
        reaction: feedback === "like" ? "thumbs_up" : "thumbs_down",
      });
    }
    const res = await postFeedback({ resultId, impression: feedback });

    if (res?.status === 204) {
      if (feedback === null) {
        const { [resultId]: _, ...remainingFeedbacks } = feedbacks;
        setFeedbacks(remainingFeedbacks);
      } else {
        setFeedbacks((prev: any) => ({ ...prev, [resultId]: feedback }));
      }
    }
  };

  return (
    <div
      className={`py-4 flex flex-col gap-4 ${disabled && "opacity-50 pointer-events-none"
        }`}
    >
      <div
        className="bg-white border-[#E2E2EA] shadow-[0px_0px_12px_0px_#A238FF0D] pt-4 rounded-2xl"
        style={{ borderWidth: "0.5px" }}
      >
        <div className="flex flex-row items-center justify-evenly px-2.5 py-1">
          <button
            onClick={() => setActiveTab("most-likely")}
            className={`${activeTab === "most-likely" &&
              "px-3 py-1 bg-[#E6FAF1] rounded-full"
              }`}
          >
            <p
              className={`text-[#101828] text-base ${activeTab === "most-likely" && "text-[#1D4D38] font-bold"
                }`}
            >
              Most Likely
            </p>
          </button>
          <button
            onClick={() => setActiveTab("expanded")}
            className={`${activeTab === "expanded" && "px-3 py-1 bg-[#E6FAF1] rounded-full"
              }`}
          >
            <p
              className={`text-[#101828] text-base ${activeTab === "expanded" && "text-[#1D4D38] font-bold"
                }`}
            >
              Expanded
            </p>
          </button>
          <button
            onClick={() => setActiveTab("cannot-miss")}
            className={`${activeTab === "cannot-miss" &&
              "px-3 py-1 bg-[#E6FAF1] rounded-full"
              }`}
          >
            <p
              className={`text-[#101828] text-base ${activeTab === "cannot-miss" && "text-[#1D4D38] font-bold"
                }`}
            >
              Can&apos;t Miss
            </p>
          </button>
        </div>
        {segregatedDiagnoses?.[activeTab]?.map((i: any, index: number) => {
          const isOpened: boolean = opened.includes(index);
          return (
            <div key={`${i}-${index}`} className="p-4">
              <button
                onClick={() => toggleOpened(index)}
                className={`w-full flex flex-col pb-4 border-b border-[#C8C8C8]`}
              >
                <div className="w-full flex flex-row justify-between ">
                  <div className="flex flex-col gap-1">
                    <div className="flex flex-row items-center gap-1">
                      <DiagnosisItem className="min-h-6 min-w-6" />
                      <p className="font-semibold text-left text-base text-[#454551]">
                        {i?.differentialDiagnosisName}
                      </p>
                    </div>
                    <div className="flex flex-row items-center gap-1.5">
                      <div className="flex flex-row items-center gap-[2px]">
                        {Array.from({ length: i?.rank ?? 0 }, () => 0).map(
                          (_, index) => (
                            <div
                              key={index.toString()}
                              className="w-3 h-1 rounded-full bg-[#EF1941]"
                            />
                          )
                        )}
                      </div>
                      <p className="font-medium text-left text-xs text-[#EF1941]">
                        {activeTab === "most-likely"
                          ? "Most Likely"
                          : activeTab === "expanded"
                            ? "Expanded"
                            : "Can't Miss"}{" "}
                        Diagnosis
                      </p>
                    </div>
                  </div>
                  <ChevronRight
                    className={`min-h-6 min-w-6 ${isOpened ? "-rotate-90" : "rotate-90"
                      }`}
                  />
                </div>
              </button>
              {isOpened && (
                <div className="flex flex-col gap-4 mt-4">
                  <p className="font-normal text-left text-sm text-[#1D1D1DA6]">
                    {i?.evidence}
                  </p>
                  {i?.labTests?.length > 0 && (
                    <div className="px-4 py-3 bg-[#FAF8F6] rounded-2xl flex flex-col gap-6">
                      <div className="flex flex-row items-center gap-1.5">
                        <LabTest className="min-h-6 min-w-6" />
                        <p className="text-base font-semibold text-left text-[#171725]">
                          Suggested Lab Tests
                        </p>
                      </div>
                      <div className="flex flex-col gap-3">
                        {i?.labTests?.map((lb: string, lbIndex: number) => (
                          <p
                            key={lbIndex}
                            className="text-[#454551] font-medium text-left text-sm"
                          >
                            {lb}
                          </p>
                        ))}
                      </div>
                    </div>
                  )}
                  {feedbacks?.[i._id] ? (
                    <div className="flex flex-row items-center gap-2.5">
                      {feedbacks?.[i._id] === "like" ? (
                        <Like className="min-h-6 min-w-6" />
                      ) : (
                        <Dislike className="min-h-6 min-w-6" />
                      )}
                      <p className="text-left text-sm text-[#1D1D1DA6] font-normal">
                        Feedback Taken
                      </p>
                      <button onClick={() => onPressFeedback(i?._id, null)}>
                        <p className="text-left text-sm text-[#1D1D1DA6] font-bold underline">
                          Undo
                        </p>
                      </button>
                    </div>
                  ) : (
                    <div className="flex flex-row items-center gap-3">
                      <p className="text-left text-sm text-[#1D1D1DA6] font-normal">
                        Was this result useful?
                      </p>
                      <button
                        onClick={() => onPressFeedback(i?._id, "dislike")}
                      >
                        <ThumbsDown className="min-h-6 min-w-6" />
                      </button>
                      <button onClick={() => onPressFeedback(i?._id, "like")}>
                        <ThumbsUp className="min-h-6 min-w-6" />
                      </button>
                    </div>
                  )}
                </div>
              )}
            </div>
          );
        })}
      </div>
      <p className="font-normal text-[#A2A2A8] text-xs text-left">
        <span className="font-semibold">Disclaimer:</span> These results are
        generated by AI and should be used as a guide, not the final source for
        patient treatment decisions.
      </p>
    </div>
  );
};

export default DiagnosisView;
